import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import clsx from "clsx";
import { AlarmClock, AtSign, Bell, Check, CheckSquare, FileQuestion, Hourglass, Inbox as InboxIcon, Scale, Siren, Stamp, X, type LucideIcon } from "lucide-react";
import { useState } from "react";
import { api, post } from "../lib/api";
import { Card, Empty, ErrorNote, fmtDate, PageHeader } from "../components/ui";
import { Due, PriorityBadge, SubProgress } from "../components/work";

export interface InboxItem {
  id: string;
  category: string;
  sourceType: string;
  sourceId: string;
  title: string;
  reason: string;
  priority: string;
  dueAt: string | null;
  link: string;
  state: string;
  seenAt: string | null;
  updatedAt: string;
  subtasks?: { total: number; done: number } | null;
}

export const CATS: Array<[string, string]> = [
  ["", "All"], ["task", "Tasks"], ["approval", "Approvals"], ["decision", "Decisions"], ["request", "Requests"], ["mention", "Mentions"], ["escalation", "Escalations"], ["waiting", "Waiting on"], ["update", "Updates"],
];

const CAT_STYLE: Record<string, { label: string; icon: LucideIcon; bg: string; fg: string }> = {
  task: { label: "Task", icon: CheckSquare, bg: "bg-brand-50", fg: "text-brand-600" },
  approval: { label: "Approval", icon: Stamp, bg: "bg-violet-50", fg: "text-violet-600" },
  decision: { label: "Decision", icon: Scale, bg: "bg-indigo-50", fg: "text-indigo-600" },
  request: { label: "Request", icon: FileQuestion, bg: "bg-sky-50", fg: "text-sky-600" },
  mention: { label: "Mention", icon: AtSign, bg: "bg-teal-50", fg: "text-teal-600" },
  escalation: { label: "Escalation", icon: Siren, bg: "bg-red-50", fg: "text-red-600" },
  waiting: { label: "Waiting on you", icon: Hourglass, bg: "bg-amber-50", fg: "text-amber-600" },
  update: { label: "Update", icon: Bell, bg: "bg-slate-100", fg: "text-slate-500" },
};

export const useInbox = (category = "") =>
  useQuery({ queryKey: ["inbox", category], queryFn: () => api<{ items: InboxItem[]; counts: Record<string, number> }>(`/api/inbox${category ? `?category=${category}` : ""}`), refetchInterval: 60_000 });

export function InboxPage() {
  const [cat, setCat] = useState("");
  const inbox = useInbox(cat);
  const qc = useQueryClient();
  const act = useMutation({
    mutationFn: ({ id, action }: { id: string; action: string }) => post(`/api/inbox/${id}`, { action }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["inbox"] }),
  });
  const counts = inbox.data?.counts ?? {};
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  return (
    <div>
      <PageHeader title="Inbox" subtitle="Everything that needs you, from every app, in one place." />
      <div className="mb-4 max-w-full overflow-x-auto"><div className="tabs" role="tablist">
        {CATS.map(([k, label]) => {
          const n = k ? counts[k] : total;
          return (
            <button key={k} role="tab" aria-selected={cat === k} className="tab" onClick={() => setCat(k)}>
              {label}{n ? <span className="count num">{n}</span> : null}
            </button>
          );
        })}
      </div></div>
      <ErrorNote error={inbox.error ?? act.error} />
      <Card flush bodyClassName="!pb-0">
        {inbox.isLoading ? <Empty>Loading…</Empty> : inbox.data?.items.length ? (
          <ul className="divide-y divide-[var(--line)]">
            {inbox.data.items.map((i) => {
              const c = CAT_STYLE[i.category] ?? CAT_STYLE.update!;
              return (
                <li key={i.id} className={clsx("group flex items-start gap-3.5 px-5 py-4 transition-colors hover:bg-[#fafbff]", !i.seenAt && "bg-brand-50/30")}>
                  <span className={clsx("mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-xl", c.bg)}><c.icon size={17} className={c.fg} /></span>
                  <Link to={i.link as "/"} className="min-w-0 flex-1" onClick={() => !i.seenAt && act.mutate({ id: i.id, action: "seen" })}>
                    <span className="flex flex-wrap items-center gap-2">
                      {!i.seenAt && <span className="h-2 w-2 rounded-full bg-brand-500" aria-label="Unread" />}
                      <span className={clsx("text-[0.9375rem] text-[var(--text)] group-hover:text-brand-700", i.seenAt ? "font-medium" : "font-bold")}>{i.title}</span>
                      <PriorityBadge p={i.priority} />
                      <SubProgress s={i.subtasks} />
                    </span>
                    <span className="muted mt-1 block text-[0.8125rem]">{i.reason}</span>
                    <span className="subtle mt-1.5 flex flex-wrap items-center gap-3 text-xs">
                      <span className={clsx("font-semibold", c.fg)}>{c.label}</span>
                      <Due at={i.dueAt} />
                      <span>{fmtDate(i.updatedAt)}</span>
                    </span>
                  </Link>
                  <span className="flex shrink-0 gap-1 opacity-70 transition-opacity group-hover:opacity-100">
                    <button className="btn btn-ghost btn-sm btn-icon !w-8" title="Snooze 1 day" aria-label="Snooze" onClick={() => act.mutate({ id: i.id, action: "snooze" })}><AlarmClock size={15} /></button>
                    {(i.category === "mention" || i.category === "update" || i.category === "escalation") ? (
                      <button className="btn btn-ghost btn-sm btn-icon !w-8" title="Done" aria-label="Mark done" onClick={() => act.mutate({ id: i.id, action: "done" })}><Check size={15} /></button>
                    ) : (
                      <button className="btn btn-ghost btn-sm btn-icon !w-8" title="Dismiss from inbox" aria-label="Dismiss" onClick={() => act.mutate({ id: i.id, action: "dismiss" })}><X size={15} /></button>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        ) : <Empty icon={<InboxIcon size={20} />}>Inbox zero. Nothing needs you right now.</Empty>}
      </Card>
    </div>
  );
}
